import { useEffect, useState } from 'react';
import { Color, COLOR_HEX } from '@otrio/engine';
import { LobbyState } from '../protocol';
import { ConnectionStatus } from '../hooks/useGameSocket';
import { COLOR_LABELS } from '../colorLabels';

interface LobbyScreenProps {
  status: ConnectionStatus;
  prefilledCode?: string;
  /** Lobby state returned by a PEEK_ROOM response */
  peekLobby: LobbyState | null;
  onCreateRoom: (name: string, color: Color) => void;
  onPeekRoom: (code: string) => void;
  onJoinRoom: (code: string, color: Color, name: string) => void;
}

export function LobbyScreen({
  status,
  prefilledCode = '',
  peekLobby,
  onCreateRoom,
  onPeekRoom,
  onJoinRoom,
}: LobbyScreenProps) {
  const [tab, setTab] = useState<'create' | 'join'>(prefilledCode ? 'join' : 'create');

  // Create-tab state
  const [createName, setCreateName] = useState('');
  const [createColor, setCreateColor] = useState<Color>('red');

  // Join-tab state
  const [joinCode, setJoinCode] = useState(prefilledCode);
  const [peeking, setPeeking] = useState(false);
  const [joinName, setJoinName] = useState('');
  const [joinColor, setJoinColor] = useState<Color | null>(null);

  // When the prefilled code is 5 chars, auto-peek on mount
  useEffect(() => {
    if (prefilledCode.length === 5 && status === 'connected') {
      setPeeking(true);
      onPeekRoom(prefilledCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Once we receive a peek result, select the first available color automatically
  useEffect(() => {
    if (peekLobby) {
      setPeeking(false);
      const available = getAvailableColors(peekLobby);
      if (available.length > 0 && !joinColor) setJoinColor(available[0]);
    }
  }, [peekLobby]);

  if (status === 'connecting') {
    return (
      <div className="lobby-screen">
        <p className="lobby-status">Connecting to server…</p>
      </div>
    );
  }

  if (status === 'error' || status === 'idle') {
    return (
      <div className="lobby-screen">
        <p className="lobby-status lobby-error">Connection lost. Please refresh.</p>
      </div>
    );
  }

  function handleCodeChange(raw: string) {
    const code = raw.toUpperCase();
    setJoinCode(code);
    setJoinColor(null);
    // Clear any previous peek when the code changes
    if (code.length < 5) onPeekRoom(''); // triggers null on parent
    if (code.length === 5) {
      setPeeking(true);
      onPeekRoom(code);
    }
  }

  function handleJoinSubmit() {
    if (!joinColor || !joinName.trim()) return;
    onJoinRoom(joinCode, joinColor, joinName.trim());
  }

  const availableColors = peekLobby ? getAvailableColors(peekLobby) : [];

  return (
    <div className="lobby-screen">
      <h1 className="lobby-title">Otrio</h1>

      <div className="lobby-tabs">
        <button
          className={`lobby-tab${tab === 'create' ? ' active' : ''}`}
          onClick={() => setTab('create')}
        >
          Create Room
        </button>
        <button
          className={`lobby-tab${tab === 'join' ? ' active' : ''}`}
          onClick={() => setTab('join')}
        >
          Join Room
        </button>
      </div>

      {tab === 'create' && (
        <div className="lobby-card">
          <label className="lobby-label">Your Name</label>
          <input
            className="player-name-input"
            type="text"
            placeholder="Enter your name"
            value={createName}
            maxLength={20}
            onChange={e => setCreateName(e.target.value)}
          />

          <label className="lobby-label">Your Colour</label>
          <div className="lobby-color-row">
            {(['red', 'green', 'purple', 'yellow'] as Color[]).map(c => (
              <button
                key={c}
                className={`lobby-color-btn${createColor === c ? ' selected' : ''}`}
                style={{ '--slot-color': COLOR_HEX[c] } as React.CSSProperties}
                onClick={() => setCreateColor(c)}
                title={COLOR_LABELS[c]}
              >
                <span className="lobby-color-dot" style={{ background: COLOR_HEX[c] }} />
                {COLOR_LABELS[c]}
              </button>
            ))}
          </div>

          <button
            className="start-btn"
            disabled={!createName.trim()}
            onClick={() => onCreateRoom(createName.trim(), createColor)}
          >
            Create Room
          </button>
        </div>
      )}

      {tab === 'join' && (
        <div className="lobby-card">
          <label className="lobby-label">Room Code</label>
          <input
            className="player-name-input lobby-code-input"
            type="text"
            placeholder="Enter 5-letter code"
            value={joinCode}
            maxLength={5}
            onChange={e => handleCodeChange(e.target.value)}
            autoFocus={!!prefilledCode}
          />

          {/* Checking room… spinner */}
          {peeking && <p className="lobby-status">Checking room…</p>}

          {/* Room not found */}
          {!peeking && joinCode.length === 5 && !peekLobby && (
            <p className="lobby-status lobby-error">Room not found.</p>
          )}

          {/* Available slots */}
          {peekLobby && (
            <>
              {availableColors.length === 0 ? (
                <p className="lobby-status lobby-error">No open slots in this room.</p>
              ) : (
                <>
                  <label className="lobby-label">Pick a Colour</label>
                  <div className="lobby-color-row">
                    {availableColors.map(c => (
                      <button
                        key={c}
                        className={`lobby-color-btn${joinColor === c ? ' selected' : ''}`}
                        style={{ '--slot-color': COLOR_HEX[c] } as React.CSSProperties}
                        onClick={() => setJoinColor(c)}
                        title={COLOR_LABELS[c]}
                      >
                        <span className="lobby-color-dot" style={{ background: COLOR_HEX[c] }} />
                        {COLOR_LABELS[c]}
                      </button>
                    ))}
                  </div>

                  <label className="lobby-label">Your Name</label>
                  <input
                    className="player-name-input"
                    type="text"
                    placeholder="Enter your name"
                    value={joinName}
                    maxLength={20}
                    onChange={e => setJoinName(e.target.value)}
                  />

                  <button
                    className="start-btn"
                    disabled={!joinColor || !joinName.trim()}
                    onClick={handleJoinSubmit}
                  >
                    Join as {joinColor ? COLOR_LABELS[joinColor] : '…'}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Returns colors that are either 'open' or 'bot' (claimable by a joiner). */
function getAvailableColors(lobby: LobbyState): Color[] {
  return lobby.slots
    .filter(s => s.type === 'open' || s.type === 'bot')
    .map(s => s.color);
}

